/**
 * O nome, as etiquetas e a linha de contexto de uma conversa, como função pura (8.3, 13.6).
 *
 * Cada conversa da lista carrega o nome do contato e as **etiquetas da equipe** (13.6), e o cabeçalho
 * do chat carrega também uma linha de contexto. Tudo sai do mesmo lugar: o que o `comunicacao` sabe
 * da réplica dele (`empresa_id`, `pessoa_id`) mais o que o `cadastro` respondeu ao SPA em lote —
 * razão social, nome do contato e os `etiqueta_ids` da Pessoa e da Empresa (AD-25).
 *
 * **As três tags automáticas saíram (13.6, chamado de 21/10, P5)** — eram do UX-DR7 e do protótipo,
 * decisão nossa, e não pedido da equipe: toda conversa de número novo ficava "Pessoa solta" para
 * sempre. O Gustavo decidiu que a tela mostra o que a equipe **etiqueta** — as etiquetas da Pessoa e
 * as da Empresa dela, sem repetir. A linha de contexto é, com Empresa, o nome dela (ou "Empresa
 * vinculada", quando o `cadastro` não o entregou); sem Empresa, "Número desconhecido · nenhuma
 * empresa vinculada", como antes. Desde a 13.11b o estado da Empresa nem existe mais.
 *
 * **Função pura e exportada porque é ela que a suíte observa** — o mesmo motivo pelo qual
 * `funilPadrao` mora fora do componente da tela do Funil.
 *
 * **O nome cai para o número quando o cadastro não o entrega**, e esse é um estado legítimo e
 * frequente: a Pessoa solta do FR-40 nasce com o número por nome, e a policy do `cadastro` pode
 * simplesmente não devolver a ficha para quem está olhando. O número é o único identificador que
 * sempre existe — é ele que o provedor manda —, e é o que o protótipo mostra na linha da Pessoa
 * solta.
 *
 * **As Empresas da conversa (13.19c2, `empresasDaConversa`) seguem o recorte do `comunicacao`**
 * (code review, A — decisão do coordenador, na lista para o Gustavo revisar): "cada uma só a sua"
 * da Pessoa compartilhada (13.19b1, dossiê de 26/10, linhas 575 e 661) vale também na tela.
 *
 * - **A gestão** vê as Empresas visíveis da Pessoa, do lote do `cadastro` (AD-25), na ordem do
 *   vínculo; sem a Pessoa no lote (a policy, ou o lote no ar), o `empresa_id` da conversa.
 * - **Quem não é gestão** vê só o `empresa_id` da conversa, que o `comunicacao` já recorta: a
 *   Empresa de que é Responsável; senão, na Pessoa **não** compartilhada, a da Pessoa; senão, nada.
 *   Nulo é "sem Empresa" — por recorte (`empresa_recortada`) ou por atraso da réplica. As Empresas
 *   que ela vê por um ramo (Grupo, PA-1, PA-3) ficam no painel Dados do contato, que é do
 *   `cadastro`, e nunca na conversa.
 *
 * A linha de contexto e a linha da lista usam a **primeira**; as etiquetas são as da Pessoa e as de
 * **todas** as Empresas da conversa (as duas listas da 13.18).
 *
 * **O caminho até o Negócio não mora aqui**: desde a 11.12 o cabeçalho do chat tem "Abrir negócio"
 * (`Conversas.tsx`), que pergunta ao `negocios` pelos em andamento da Empresa. Esta função continua
 * só dizendo de quem é a conversa.
 */

import type { Conversa } from './api'

/** A linha de contexto de quem não tem Empresa nenhuma por trás. */
export const CONTEXTO_SEM_EMPRESA = 'Número desconhecido · nenhuma empresa vinculada'

/**
 * A linha de contexto quando a conversa tem Empresa e o `cadastro` não entregou o nome dela (o lote
 * no ar, ou a ficha que a RLS não devolve a quem olha) — D-10 da 13.6. Chamava-se
 * `CONTEXTO_SEM_RAZAO` até a 13.7, quando o nome da Empresa passou a ser o nome exibido.
 */
export const CONTEXTO_SEM_NOME_DA_EMPRESA = 'Empresa vinculada'

/**
 * O que o `cadastro` respondeu ao SPA, em lote — os rótulos e as etiquetas, e nada mais.
 *
 * Mapas, e não a resposta inteira das duas rotas: o que esta função decide depende de **nome**,
 * **nome da Empresa** e **etiquetas**, e receber as fichas completas convidaria a próxima regra a se
 * pendurar num campo que a tela não deveria estar olhando.
 */
export interface RotulosDoCadastro {
  /** `pessoa_id -> nome`. O que a policy não devolveu simplesmente não está aqui. */
  pessoas: ReadonlyMap<string, string>
  /**
   * `empresa_id -> nome exibido` (13.7: o nome fantasia, e a razão social sem ele), pela mesma
   * regra.
   */
  empresas: ReadonlyMap<string, string>
  /**
   * `empresa_id -> razão social` (13.7): a busca da lista acha a Empresa também por ela, que a
   * linha não mostra mais quando há nome fantasia. Ausente vale "não procura por ela".
   */
  razoesDasEmpresas?: ReadonlyMap<string, string>
  /** `pessoa_id -> etiqueta_ids` (13.6). Ausente vale vazio (o lote ainda no ar). */
  etiquetasDasPessoas?: ReadonlyMap<string, readonly string[]>
  /** `empresa_id -> etiqueta_ids` (13.6), pela mesma regra. */
  etiquetasDasEmpresas?: ReadonlyMap<string, readonly string[]>
  /**
   * `pessoa_id -> empresa_ids` (13.19c2): as Empresas visíveis de cada Pessoa do lote, na ordem do
   * vínculo. A Pessoa fora do mapa não veio do lote (a policy, ou o lote no ar); a de lista vazia é
   * a solta. Ausente vale "nenhuma Pessoa veio".
   */
  empresasDasPessoas?: ReadonlyMap<string, readonly string[]>
  /**
   * `pessoa_id -> empresa_ids_editaveis` (code review da 13.19c2, C): as Empresas em que quem olha
   * cria Negócio — as do "+ Negócio" da conversa. Pela mesma regra de `empresasDasPessoas`.
   */
  editaveisDasPessoas?: ReadonlyMap<string, readonly string[]>
  /**
   * `pessoa_id -> pode_editar` (13.20c, code review A4): falso para a Pessoa lida só pelo Negócio
   * sem Empresa que quem olha recebeu. Fora do mapa é "não sei".
   */
  podeEditarDasPessoas?: ReadonlyMap<string, boolean>
  /**
   * Se quem olha é a gestão (escopo `todas`): só ela vê na conversa as Empresas visíveis do
   * `cadastro` (code review da 13.19c2, A). Ausente vale "não é".
   */
  gestao?: boolean
}

/** O que a tela desenha de uma conversa, além das mensagens. */
export interface ContextoDaConversa {
  /** O nome do contato — ou o número, quando nem o cadastro nem o WhatsApp dão nome com letra. */
  nome: string
  /** As iniciais do avatar. `?` quando o nome é um número, como no protótipo. */
  iniciais: string
  /**
   * As etiquetas da equipe da Pessoa (13.6), do catálogo `pessoa` (13.18). A tela as resolve pelo
   * catálogo dela (nome e cor).
   */
  etiquetasDaPessoa: readonly string[]
  /**
   * As etiquetas das Empresas da conversa, do catálogo `empresa` (13.18) — uma lista à parte. Desde
   * a 13.19c2, as de todas as Empresas visíveis da Pessoa, sem repetir, na ordem delas.
   */
  etiquetasDaEmpresa: readonly string[]
  /** A linha de contexto do cabeçalho do chat: a Empresa, ou a ausência dela. */
  contexto: string
  /**
   * O nome da Empresa (13.7: o nome exibido) para a linha da **lista**, embaixo do nome (chamado de
   * 21/10/2026, P12) — e o que a busca da lista também casa.
   *
   * Só existe com Empresa **e** o nome entregue pelo `cadastro`: a lista não repete "Número
   * desconhecido…" nem "Empresa vinculada". Também some quando ele é o próprio nome do contato (o
   * MEI, ou o contato cadastrado com o nome da marca), para a linha não dizer a mesma coisa duas
   * vezes.
   */
  empresa?: string
  /** A razão social da Empresa (13.7), só para a busca da lista; a tela não a desenha. */
  razaoSocialDaEmpresa?: string
}

/**
 * O nome da Empresa que a lista mostra, ou nada quando ele não veio, veio em branco ou repete o nome
 * do contato.
 *
 * A comparação é sem caixa e sem espaço nas pontas: `CLÁUDIA BASTOS ` é o mesmo que `Cláudia Bastos`
 * para quem lê.
 */
function empresaDaLista(nomeDaEmpresa: string | undefined, nome: string): string | undefined {
  if (nomeDaEmpresa === undefined || nomeDaEmpresa.trim() === '') {
    return undefined
  }
  const igualAoNome =
    nomeDaEmpresa.trim().toLocaleLowerCase('pt-BR') === nome.trim().toLocaleLowerCase('pt-BR')
  return igualAoNome ? undefined : nomeDaEmpresa
}

/** As iniciais de quem não tem nome com letra nenhuma — a linha da Pessoa solta do protótipo. */
const SEM_INICIAIS = '?'

/**
 * O nome que o `cadastro` deu, ou nada quando ele não tem letra nenhuma.
 *
 * A Pessoa solta do FR-40 nasce com o próprio número por nome (`pessoa_solta.py`): esse "nome" é
 * placeholder, e não pode vencer o que o contato usa no WhatsApp (13/10/2026). O critério de "tem
 * letra" é o mesmo de `iniciaisDoContato`.
 */
function nomeDoCadastro(conversa: Conversa, rotulos: RotulosDoCadastro): string | undefined {
  const nome = conversa.pessoa_id === null ? undefined : rotulos.pessoas.get(conversa.pessoa_id)
  return nome !== undefined && temLetra(nome) ? nome : undefined
}

/** "Tem letra" — o critério único de nome de verdade, o mesmo de `iniciaisDoContato`. */
function temLetra(nome: string): boolean {
  return /\p{L}/u.test(nome)
}

/**
 * O nome do contato que veio do WhatsApp, ou nada — `undefined` num contrato antigo, sem o campo.
 *
 * Desde 13/10/2026 ele pode ser o nome que a **Vendedora salvou na agenda** (`wa_contactName`, o
 * primeiro degrau do serviço), e não só o que o contato escolheu para si. Sem letra é ausência,
 * pela mesma regra do cadastro: um número aqui cai no número formatado, e não aparece cru.
 */
function nomeNoWhatsapp(conversa: Conversa): string | undefined {
  const nome = (conversa as { nome_no_whatsapp?: string | null }).nome_no_whatsapp ?? null
  return nome === null || !temLetra(nome) ? undefined : nome
}

/**
 * O número como o WhatsApp o escreve: `+55 11 99918-4967`.
 *
 * Só o Brasil ganha a separação em DDD e assinante (é a carteira inteira da Orvalho); um número
 * de outro país sai como `+` e os dígitos — nunca uma máscara errada. Grafia que não é só dígito
 * volta como veio: melhor o cru do que um número inventado.
 */
export function formatarNumeroDeWhatsapp(numero: string): string {
  const digitos = numero.replace(/\D/g, '')
  if (digitos === '' || digitos !== numero.trim().replace(/^\+/, '')) {
    return numero
  }
  if (digitos.startsWith('55') && (digitos.length === 12 || digitos.length === 13)) {
    const ddd = digitos.slice(2, 4)
    const assinante = digitos.slice(4)
    const corte = assinante.length - 4
    return `+55 ${ddd} ${assinante.slice(0, corte)}-${assinante.slice(corte)}`
  }
  return `+${digitos}`
}

/**
 * As iniciais de um nome — no máximo duas, do primeiro e do último termo.
 *
 * **Um nome que não tem letra nenhuma vira `?`**, e é assim que o protótipo desenha a linha da
 * Pessoa solta: o "nome" dela é o próprio número, e "55" num avatar não identifica ninguém.
 *
 * Ela é exportada porque a lista e o cabeçalho a usam, e porque o caso do `?` é uma afirmação da
 * suíte — derivá-la duas vezes daria duas respostas no primeiro nome estranho.
 */
export function iniciaisDoContato(nome: string): string {
  const letras = nome
    .split(/\s+/)
    .map((termo) => termo.trim().charAt(0))
    .filter(temLetra)
    .map((letra) => letra.toLocaleUpperCase('pt-BR'))
  const primeira = letras.at(0)
  const ultima = letras.at(-1)
  if (primeira === undefined || ultima === undefined) {
    return SEM_INICIAIS
  }
  return primeira === ultima ? primeira : `${primeira}${ultima}`
}

/**
 * As Empresas da conversa, na ordem em que a tela as usa (13.19c2, code review A): para a gestão,
 * as visíveis da Pessoa no lote do `cadastro` (vazia na solta) e, sem a Pessoa no lote, o
 * `empresa_id` da conversa; para quem não é gestão, só o `empresa_id` da conversa, que o
 * `comunicacao` recorta por quem lê. Nunca nomeia uma Empresa que não veio de um dos dois.
 */
export function empresasDaConversa(
  conversa: Conversa,
  rotulos: Pick<RotulosDoCadastro, 'empresasDasPessoas' | 'gestao'>,
): readonly string[] {
  if (rotulos.gestao === true && conversa.pessoa_id !== null) {
    const daPessoa = rotulos.empresasDasPessoas?.get(conversa.pessoa_id)
    if (daPessoa !== undefined) {
      return daPessoa
    }
  }
  return conversa.empresa_id === null ? [] : [conversa.empresa_id]
}

/**
 * As etiquetas da conversa em **duas listas**, uma por catálogo (13.18): as da Pessoa e as das
 * Empresas. Não se juntam por id — são catálogos diferentes, e "VIP" de Pessoa e "VIP" de Empresa
 * saem as duas. A ordem de exibição (as da Pessoa primeiro, D-9 da 13.6) é de `ChipsDaConversa`.
 * Desde a 13.19c2 as de Empresa são as de todas as Empresas visíveis, sem repetir.
 */
function etiquetasDaConversa(
  conversa: Conversa,
  empresas: readonly string[],
  rotulos: RotulosDoCadastro,
): Pick<ContextoDaConversa, 'etiquetasDaPessoa' | 'etiquetasDaEmpresa'> {
  return {
    etiquetasDaPessoa:
      conversa.pessoa_id === null
        ? []
        : (rotulos.etiquetasDasPessoas?.get(conversa.pessoa_id) ?? []),
    etiquetasDaEmpresa: [
      ...new Set(empresas.flatMap((id) => rotulos.etiquetasDasEmpresas?.get(id) ?? [])),
    ],
  }
}

/**
 * O nome, as etiquetas e a linha de contexto daquela conversa.
 *
 * **Dois ramos, e o estado não decide nenhum** (13.6):
 *
 * 1. **Com Empresa** (`empresasDaConversa`, 13.19c2): a linha de contexto é o nome da **primeira**
 *    Empresa que o `cadastro` entregou (13.7: o nome exibido), ou "Empresa vinculada" quando ele não
 *    veio; o nome entra também embaixo do nome do contato, na lista. As etiquetas de Empresa são as
 *    de todas as visíveis.
 * 2. **Sem Empresa conhecida**. Ele cobre conversa sem Pessoa, a Pessoa solta, a Pessoa que a réplica
 *    não viu e a Pessoa cuja Empresa ainda não chegou: "o cadastro ainda não disse de quem é isto".
 *    As etiquetas da Pessoa (a solta pode ter) continuam aparecendo. **Com a Empresa recortada**
 *    (`empresa_recortada`: a Pessoa compartilhada, para quem não é Responsável da Empresa dela), a
 *    linha é o número formatado — sem "desconhecido" e sem "nenhuma empresa", e sem dizer que há
 *    uma Empresa escondida.
 */
export function contextoDaConversa(
  conversa: Conversa,
  rotulos: RotulosDoCadastro,
): ContextoDaConversa {
  /*
   * Três degraus para o nome (quarta passada, 29/09/2026): o do cadastro, quando a Pessoa existe, a
   * policy a devolveu e o nome tem letra (o número por nome da Pessoa solta é placeholder); senão o
   * do WhatsApp (`nome_no_whatsapp`, do provedor: o nome salvo na agenda da Vendedora ou o que o
   * contato escolheu para si), também só com letra; senão o número, formatado como o próprio
   * WhatsApp o escreve. O do cadastro vence porque é o nome que a Orvalho conhece.
   */
  const nome =
    nomeDoCadastro(conversa, rotulos) ??
    nomeNoWhatsapp(conversa) ??
    formatarNumeroDeWhatsapp(conversa.numero)
  const empresas = empresasDaConversa(conversa, rotulos)
  const [primeira] = empresas
  const nomeDaEmpresa = primeira === undefined ? undefined : rotulos.empresas.get(primeira)
  const razaoSocialDaEmpresa =
    primeira === undefined ? undefined : rotulos.razoesDasEmpresas?.get(primeira)

  const comum = {
    nome,
    iniciais: iniciaisDoContato(nome),
    ...etiquetasDaConversa(conversa, empresas, rotulos),
  }

  if (primeira === undefined) {
    /* A Pessoa conhecida com a Empresa recortada (a compartilhada, para quem não é Responsável da
       Empresa dela; code review da 13.19c2): "Número desconhecido · nenhuma empresa vinculada"
       seria falso nas duas metades. A linha diz só o número, como o WhatsApp — sem dizer que
       existe uma Empresa que quem olha não vê. */
    const recortada = (conversa as { empresa_recortada?: boolean }).empresa_recortada === true
    return {
      ...comum,
      contexto: recortada ? formatarNumeroDeWhatsapp(conversa.numero) : CONTEXTO_SEM_EMPRESA,
    }
  }
  const empresa = empresaDaLista(nomeDaEmpresa, nome)
  return {
    ...comum,
    ...(empresa === undefined ? {} : { empresa }),
    ...(razaoSocialDaEmpresa === undefined ? {} : { razaoSocialDaEmpresa }),
    contexto:
      nomeDaEmpresa === undefined || nomeDaEmpresa.trim() === ''
        ? CONTEXTO_SEM_NOME_DA_EMPRESA
        : nomeDaEmpresa,
  }
}
